import { useEffect, useState } from 'react';

// === knobs for sizing ===
const TB_BTN = 'w-10 h-10';      // button size 
const TB_ICON = 'text-2xl';       // icon size
const TB_GAP  = 'space-x-3';     // spacing between icons

// Single taskbar icon
const TaskbarIcon = ({ icon, appName, onOpenApp, isOpen, onClick, iconColor }) => {
  const handleRightClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const contextMenuEvent = new CustomEvent('showContextMenu', {
      detail: { event: e, type: 'taskbar', data: { appName } }
    })
    document.dispatchEvent(contextMenuEvent)
  }

  return (
    <div
      className={`taskbar-icon ${TB_BTN} flex items-center justify-center rounded-md hover:bg-white/10 ${
        isOpen ? 'bg-white/20 shadow-lg' : ''
      }`}
      onClick={onClick || (() => onOpenApp(appName))}
      onContextMenu={handleRightClick}
    >
      <i className={`${icon} ${TB_ICON}`} style={{color: iconColor}}></i>
      {isOpen && <div className="absolute bottom-1 w-1 h-1 bg-white/60 rounded-full"></div>}
    </div>
  );
};

const Taskbar = ({ onToggleStartMenu, onOpenApp, windows, onFocusWindow, onMinimizeWindow, pinnedApps = [] }) => {
  const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const [theme, setTheme] = useState(localStorage.getItem('pp_icon_theme') || 'neon')
  const [userColor, setUserColor] = useState(localStorage.getItem('pp_icon_user_color') || '#f472b6')
  useEffect(()=>{const handler=(e)=>{if(e.detail?.id) setTheme(e.detail.id); if(e.detail?.color) setUserColor(e.detail.color)};window.addEventListener('iconThemeChange',handler);return()=>window.removeEventListener('iconThemeChange',handler)},[])

  const palettes = {
    pinkPixel: ['#f472b6', '#ec4899', '#db2777', '#be185d', '#9d174d'],
    cloudwerx: ['#38bdf8', '#0ea5e9', '#0284c7', '#0369a1', '#075985'],
    monotone: ['#ffffff'],
    neon: ['#ff00ff', '#00eaff', '#39ff14', '#fffb00', '#ff007f'],
    pastel: ['#fbcfe8', '#bfdbfe', '#fde68a', '#c7d2fe', '#bbf7d0'],
    rainbow: ['#ef4444', '#f59e0b', '#eab308', '#10b981', '#3b82f6', '#6366f1', '#db2777'],
    userColor: [userColor]
  }
  const palette = palettes[theme] || palettes.neon
  const colorForIndex = (i) => palette[i % palette.length]

  // App icons mapping (base classes only)
  const appIconsMap = {
    'explorer': 'fas fa-folder',
    'browser': 'fas fa-globe', 
    'store': 'fas fa-shopping-bag',
    'notepad': 'fas fa-file-text',
    'spotify': 'fab fa-spotify',
    'calculator': 'fas fa-calculator',
    'weather': 'fas fa-cloud-sun',
    'calendar': 'fas fa-calendar',
    'settings': 'fas fa-cog',
    'photos': 'fas fa-images',
    'games': 'fas fa-gamepad',
    'catalog': 'fas fa-shopping-bag',
    'crumb': 'fas fa-robot',
    'mail': 'fas fa-envelope',
    'youtube': 'fab fa-youtube'
  }
  
  // Convert pinned app names to full app objects
  const pinnedAppObjects = pinnedApps.map(appName => ({
    icon: appIconsMap[appName] || 'fas fa-window-maximize text-white',
    appName
  }));

  return (
    <div className="taskbar absolute bottom-0 left-0 right-0 glass-effect rounded-2xl mb-1 mx-auto w-fit min-w-[360px] max-w-[92vw] h-14 flex items-center justify-center px-3 sm:px-5">
      <div className={`flex items-center ${TB_GAP}`}>
        {/* Start */}
        <div className={`taskbar-icon ${TB_BTN} flex items-center justify-center rounded-md hover:bg-white/10`} onClick={onToggleStartMenu}>
          <i className={`fas fa-star text-3xl`} style={{color: colorForIndex(0)}}></i>
        </div>
		
		{/* Separator */}
        <div className="h-6 w-px bg-white/20 mx-1"></div>

        {/* Search */}
        <div className={`taskbar-icon ${TB_BTN} flex items-center justify-center rounded-md hover:bg-white/10`}>
          <i className={`fas fa-search text-xl`} style={{color: colorForIndex(1)}}></i>
        </div>

        {/* Pinned apps */}
        {pinnedAppObjects.map((app, index) => {
          const appWindow = windows.find(w => w.appName === app.appName);
          const isOpen = !!appWindow;

          const handlePinnedAppClick = () => {
            if (!appWindow) onOpenApp(app.appName);
            else if (appWindow.isMinimized) onMinimizeWindow(appWindow.id);
            else if (appWindow.isFocused) onMinimizeWindow(appWindow.id);
            else onFocusWindow(appWindow.id);
          };

          return (
            <TaskbarIcon
              key={index}
              icon={`${app.icon}`}
              appName={app.appName}
              onOpenApp={onOpenApp}
              isOpen={isOpen}
              onClick={handlePinnedAppClick}
              iconColor={colorForIndex(2 + index)}
            />
          );
        })}

        {/* Open windows not pinned */}
        {windows
          .filter(w => !pinnedApps.includes(w.appName))
          .map((window) => {
            const appIcons = {
			  explorer: 'fas fa-folder text-pink-500',
              notepad: 'fas fa-file-text text-cyan-500',
              weather: 'fas fa-cloud-sun text-blue-500',
              calendar: 'fas fa-calendar text-green-500',
              settings: 'fas fa-cog text-purple-500',
              photos: 'fas fa-images text-orange-500',
			  games: 'fas fa-gamepad text-cyan-500',
			  catalog: 'fas fa-shopping-bag text-purple-500'
            };

            const handleWindowRightClick = (e) => {
              e.preventDefault()
              e.stopPropagation()
              const contextMenuEvent = new CustomEvent('showContextMenu', {
                detail: { event: e, type: 'taskbar', data: { appName: window.appName } }
              })
              document.dispatchEvent(contextMenuEvent)
            }

            return (
              <div
                key={window.id}
                className={`taskbar-icon ${TB_BTN} flex items-center justify-center rounded-md hover:bg-white/10 ${
                  window.isFocused ? 'bg-white/20' : window.isMinimized ? 'bg-white/10' : 'bg-white/5'
                }`}
                onClick={() => (window.isMinimized ? onMinimizeWindow(window.id) : onFocusWindow(window.id))}
                onContextMenu={handleWindowRightClick}
              >
                <i className={`${appIcons[window.appName] || 'fas fa-window-maximize'} ${TB_ICON}`} style={{color: colorForIndex(7 + Object.keys(windows).indexOf(window.id))}}></i>
              </div>
            );
          })}

        {/* Separator */}
        <div className="h-6 w-px bg-white/20 mx-1"></div>

        {/* Time */}
        <div className={`taskbar-icon px-2 h-10 flex items-center justify-center rounded-md hover:bg-white/10 ml-4 mr-2`}>
          <span className="text-md" style={{color: colorForIndex(8)}}>{currentTime}</span>
        </div>

        {/* Notifications */}
        <div className={`taskbar-icon ${TB_BTN} flex items-center justify-center rounded-md hover:bg-white/10`}>
          <i className="fas fa-bell text-lg" style={{color: colorForIndex(9)}}></i>
        </div>
      </div>
    </div>
  );
};

export default Taskbar;
